import { Alert, Card, DatePicker, Space, Switch, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Button } from '~/app/components/common/Button';
import type { WizardData } from '../types';
import type { StepAssignableAssetRow } from './types';
import { StepAssetsConflictModal } from './components/StepAssetsConflictModal/StepAssetsConflictModal';
import { StepAssetsFilters } from './components/StepAssetsFilters/StepAssetsFilters';
import { StepAssetsStartDateHelp } from './components/StepAssetsStartDateHelp/StepAssetsStartDateHelp';
import { useStepAssets } from './hooks/useStepAssets';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';

const { Paragraph, Text } = Typography;

export interface StepAssetsProps {
  data: WizardData;
  onChange: (patch: Partial<WizardData>) => void;
  isPreviewing: boolean;
  conflictModalOpen: boolean;
  onConflictModalOpenChange: (open: boolean) => void;
  /** Called after Apply Decisions so the parent can advance to Review. */
  onConflictsResolved?: () => void;
}

export function StepAssets({
  data,
  onChange,
  isPreviewing,
  conflictModalOpen,
  onConflictModalOpenChange,
  onConflictsResolved,
}: StepAssetsProps) {
  const { t } = useTranslation('maintenance');
  const {
    skipAssets,
    filters,
    assignableAssets,
    isLoading,
    filteredMatchingCount,
    page,
    pageSize,
    unresolvedCount,
    assetColumns,
    conflictColumns,
    filterOptions,
    handleSkipChange,
    handleFilterChange,
    handlePageChange,
    handleStartDateChange,
    handleSelectionChange,
    handleSelectAllMatching,
    handleClearSelection,
    setDecision,
    applyConflictDecisions,
  } = useStepAssets({
    data,
    onChange,
    conflictModalOpen,
    onConflictModalOpenChange,
  });

  const handleApplyDecisions = () => {
    if (onConflictsResolved) {
      onConflictsResolved();
      return;
    }
    applyConflictDecisions();
  };

  return (
    <Card>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 16,
            width: '100%',
          }}
        >
          <div style={{ flexShrink: 0 }}>
            <Switch checked={skipAssets} onChange={handleSkipChange} />
            <Text type="secondary" style={{ marginLeft: 8 }}>
              {t('wizard.assets.skipAssets')}
            </Text>
          </div>
          {!skipAssets && <StepAssetsStartDateHelp />}
        </div>

        {!skipAssets && (
          <>
            <Paragraph type="secondary">
              {t('wizard.assets.selectAssets')}
            </Paragraph>

            <DatePicker
              style={{ width: 220 }}
              placeholder={t('wizard.assets.startDate')}
              value={data.startDate ? dayjs(data.startDate) : null}
              onChange={(_, dateString) => handleStartDateChange(dateString)}
            />

            <StepAssetsFilters
              filters={filters}
              onChange={handleFilterChange}
              assetTypeOptions={filterOptions.assetTypeOptions}
              assetModelOptions={filterOptions.assetModelOptions}
              landlordOptions={filterOptions.landlordOptions}
              siteOptions={filterOptions.siteOptions}
            />

            <Space wrap>
              <Button
                onClick={handleSelectAllMatching}
                disabled={filteredMatchingCount === 0}
              >
                {t('wizard.assets.selectAllMatching', {
                  count: filteredMatchingCount,
                })}
              </Button>
              <Button
                onClick={handleClearSelection}
                disabled={data.assetIds.length === 0}
              >
                {t('wizard.assets.clearSelection')}
              </Button>
              {data.assetIds.length > 0 && (
                <Text>
                  {t('wizard.assets.selectedCount', {
                    count: data.assetIds.length,
                  })}
                </Text>
              )}
            </Space>

            <EvimoTable<StepAssignableAssetRow>
              rowKey="id"
              loading={isLoading || isPreviewing}
              columns={assetColumns}
              dataSource={assignableAssets}
              size="small"
              pagination={{
                current: page,
                pageSize,
                total: filteredMatchingCount,
                showSizeChanger: false,
                onChange: (nextPage) => handlePageChange(nextPage),
              }}
              locale={{ emptyText: t('wizard.assets.noAssets') }}
              rowSelection={{
                selectedRowKeys: data.assetIds,
                onChange: (keys) =>
                  void handleSelectionChange(keys as string[]),
                preserveSelectedRowKeys: true,
              }}
            />

            {data.conflictsChecked && data.checkedConflicts.length === 0 && (
              <Tag color="success">{t('wizard.assets.conflicts.none')}</Tag>
            )}

            {data.conflictsChecked && data.checkedConflicts.length > 0 && (
              <Alert
                type={unresolvedCount === 0 ? 'success' : 'warning'}
                showIcon
                message={t('wizard.assets.conflicts.pendingSummary', {
                  count: data.checkedConflicts.length,
                  unresolved: unresolvedCount,
                })}
                action={
                  unresolvedCount > 0 ? (
                    <Button
                      size="small"
                      onClick={() => onConflictModalOpenChange(true)}
                    >
                      {t('wizard.assets.conflicts.resolve')}
                    </Button>
                  ) : undefined
                }
              />
            )}

            {data.replaceAssetIds.length > 0 && (
              <Text type="secondary">
                {t('wizard.review.replacingCount', {
                  count: data.replaceAssetIds.length,
                })}
              </Text>
            )}
          </>
        )}
      </Space>

      <StepAssetsConflictModal
        open={conflictModalOpen}
        data={data}
        unresolvedCount={unresolvedCount}
        columns={conflictColumns}
        onCancel={() => onConflictModalOpenChange(false)}
        onApply={handleApplyDecisions}
        onDecision={setDecision}
      />
    </Card>
  );
}
